import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { expenseApproverApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import ExpenseStatusBadge from '../../components/expense/ExpenseStatusBadge.jsx';
import Skeleton from '../../components/ui/Skeleton.jsx';
import { History, ArrowRight, User, Calendar, DollarSign, CheckCircle2 } from 'lucide-react';

// Read-only history of expense reports this approver has approved a step
// on. Scoped entirely server-side (api/approver-expense-history) — a
// report only shows up here if the caller's email appears in its
// chainSnapshot with status 'approved'. Clicking a row opens the same
// review page used for live approvals; it renders in read-only mode
// whenever canApprove is false (already-decided reports), so no
// duplicate detail view is needed here.
export default function ExpenseApprovalHistoryPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await expenseApproverApi.getHistory();
        setReports(res.reports || []);
      } catch (err) {
        toast.error(`Failed to load approval history: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight mb-1">Approval History</h1>
      <p className="text-muted-foreground mb-6">
        {reports.length} expense report{reports.length !== 1 ? 's' : ''} you've approved
      </p>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <History className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>You haven't approved any expense reports yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <Card
              key={report.rowKey}
              className="cursor-pointer hover:border-profun-blue/20"
              onClick={() =>
                navigate(
                  `/expense-approvals/${encodeURIComponent(report.partitionKey)}/${encodeURIComponent(report.rowKey)}`
                )
              }
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-semibold text-profun-cyan">{report.claimRef}</span>
                      <ExpenseStatusBadge status={report.status} />
                    </div>
                    <p className="font-medium">{report.claimPurpose}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {report.requesterName}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {(report.totalClaimedSAR || 0).toFixed(2)} SAR
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Approved {report.myDecidedAt ? new Date(report.myDecidedAt).toLocaleDateString() : '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Submitted {report.submittedAt ? new Date(report.submittedAt).toLocaleDateString() : '—'}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
